import React, { useState, useEffect } from "react";
import { makeRequest2 } from '../../../utils/makeRequest';
import axios from "axios";
import { toastify } from '../../../utils/toast';
import { getItem } from "../../../utils/localStorage";
import { addBankDetailsURL } from '../../../utils/urls';

const BankDetailsTab = ({ onComplete, isCompleted, existingData }) => {
    const [loading, setLoading] = useState(false);
    const [isLoadingDefaults, setIsLoadingDefaults] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [activeSection, setActiveSection] = useState('bank');
    const [apiError, setApiError] = useState(null);

    const [sectionCompleted, setSectionCompleted] = useState({
        bank: false,
        payment: false
    });

    const [bankDetails, setBankDetails] = useState({
        accountName: '',
        accountNumber: '',
        bankName: '',
        branchName: '',
        branchCode: '',
        bankCode: '',
        swiftCode: '',
        isDefault: false
    });

    // Payment details state
    const [paymentDetails, setPaymentDetails] = useState({
        shortCode: '',
        passkey: '',
        authorizationKey: '',
        module: ''
    });

    // Check if either section is completed and trigger onComplete
    useEffect(() => {
        const eitherSectionCompleted = sectionCompleted.bank || sectionCompleted.payment;
        if (eitherSectionCompleted && !isCompleted) {
            onComplete();
        }
    }, [sectionCompleted, isCompleted, onComplete]);

    // Load existing data when component mounts or existingData changes
    useEffect(() => {
        if (existingData) {
            // Load bank details if they exist
            if (existingData.bankDetails) {
                setBankDetails({
                    accountName: existingData.bankDetails.accountName || '',
                    accountNumber: existingData.bankDetails.accountNumber || '',
                    bankName: existingData.bankDetails.bankName || '',
                    branchName: existingData.bankDetails.branchName || '',
                    branchCode: existingData.bankDetails.branchCode || '',
                    bankCode: existingData.bankDetails.bankCode || '',
                    swiftCode: existingData.bankDetails.swiftCode || '',
                    isDefault: existingData.bankDetails.isDefault || false
                });

                // Mark bank section as completed
                if (existingData.bankDetails.accountName && existingData.bankDetails.accountNumber && existingData.bankDetails.bankName) {
                    setSectionCompleted(prev => ({ ...prev, bank: true }));
                }
            }

            // Load payment details if they exist
            if (existingData.paymentDetails) {
                setPaymentDetails({
                    shortCode: existingData.paymentDetails.shortCode || '',
                    passkey: existingData.paymentDetails.passkey || '',
                    authorizationKey: existingData.paymentDetails.authorizationKey || '',
                    module: existingData.paymentDetails.module || ''
                });

                // Mark payment section as completed
                if (existingData.paymentDetails.shortCode && existingData.paymentDetails.passkey && existingData.paymentDetails.authorizationKey && existingData.paymentDetails.module) {
                    setSectionCompleted(prev => ({ ...prev, payment: true }));
                }
            }
        }
    }, [existingData]);

    // Calculate completion percentage for this tab
    const getTabCompletionPercentage = () => {
        const completedSections = Object.values(sectionCompleted).filter(Boolean).length;
        const percentage = Math.round((completedSections / 2) * 100);
        return percentage;
    };

    const validateBankDetails = () => {
        return bankDetails.accountName &&
            bankDetails.accountNumber &&
            bankDetails.bankName &&
            bankDetails.branchName;
    };

    const validatePaymentDetails = () => {
        return paymentDetails.shortCode &&
            paymentDetails.passkey &&
            paymentDetails.authorizationKey &&
            paymentDetails.module;
    };

    const fetchDefaultPaymentDetails = async () => {
        setIsLoadingDefaults(true);
        try {
            // Simulate API call to fetch default payment details
            // Replace with actual API call
            const defaultDetails = {
                shortCode: 'default_shortcode',
                passkey: 'default_passkey',
                authorizationKey: 'default_auth_key',
                module: 'All'
            };
            setPaymentDetails(defaultDetails);
            toastify('Default payment details loaded successfully', 'success');
        } catch (error) {
            toastify('Error loading default payment details', 'error');
        } finally {
            setIsLoadingDefaults(false);
        }
    };

    const handleMpesaDetailsSubmit = async () => {
        setIsSubmitting(true);
        setApiError(null);

        try {
            // Validate required fields
            if (!paymentDetails.shortCode || !paymentDetails.module) {
                throw new Error("Short code and module are required fields");
            }

            // For new payment details, all fields are required
            if (!paymentDetails.passkey || !paymentDetails.authorizationKey) {
                throw new Error("All fields are required for payment details");
            }

            const facilityId = await getItem('selectedFacilityId');

            const data = {
                facility: facilityId,
                shortCode: paymentDetails.shortCode,
                passkey: paymentDetails.passkey,
                authorizationKey: paymentDetails.authorizationKey,
                module: paymentDetails.module,
            };

            // First request - to main server
            const response = await makeRequest2(
                '/api/core/facility_payment_details/add_payment_details',
                'POST',
                data
            );

            if (response.success) {
                toastify('Payment details added successfully to main server', 'success');

                // Second request - to MPesa service
                try {
                    const mpesaData = {
                        shortCode: paymentDetails.shortCode,
                        passkey: paymentDetails.passkey,
                        authorizationKey: paymentDetails.authorizationKey,
                        module: paymentDetails.module,
                        facilityId: await getItem('selectedFacilityId'),
                    };

                    const mpesaResponse = await axios.post(
                        process.env.REACT_APP_MPESA_PAYMENT_DETAILS_URL,
                        mpesaData,
                        {
                            headers: {
                                "Content-Type": "application/json",
                            },
                            timeout: 10000, // 10 second timeout
                        },
                    );

                    if (mpesaResponse.status === 200 || mpesaResponse.status === 201) {
                        toastify("Payment details have been added to MPesa", "success");
                    } else {
                        throw new Error(
                            `MPesa service returned status: ${mpesaResponse.status}`,
                        );
                    }
                } catch (mpesaErr) {
                    // Still continue even if MPesa sync fails, but notify user
                    const errorMessage =
                        mpesaErr.response?.data?.message ||
                        mpesaErr.response?.data?.error ||
                        mpesaErr.message ||
                        "Unknown error occurred";

                    toastify(
                        `Warning: Failed to add payment details to MPesa - ${errorMessage}. Main server update was successful.`,
                        "warning",
                    );
                }

                // Mark payment section as completed
                setSectionCompleted(prev => ({ ...prev, payment: true }));

                if (onComplete) {
                    onComplete();
                }

                // Clear form
                setPaymentDetails({
                    shortCode: '',
                    passkey: '',
                    authorizationKey: '',
                    module: ''
                });

            } else {
                throw new Error(
                    response.error || 'Failed to save payment details to main server',
                );
            }
        } catch (err) {
            setApiError(err.message);
            toastify(err.message, "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        setPaymentDetails({
            shortCode: '',
            passkey: '',
            authorizationKey: '',
            module: ''
        });
    };

    const handleBankDetailsSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const facilityId = await getItem('selectedFacilityId');
            if (!validateBankDetails()) {
                toastify('Please fill in all required fields', 'error');
                return;
            }

            const url = `${addBankDetailsURL}/${facilityId}`;
            const method = 'POST';
            const response = await makeRequest2(url, method, bankDetails);

            if (response && (response.success || response.message)) {
                toastify('Bank details added successfully','success');

                // Mark bank section as completed
                setSectionCompleted(prev => ({ ...prev, bank: true }));

            } else {
                const errorMessage = response?.error || response?.message || 'Operation failed';
                toastify(errorMessage, 'error');
            }
        } catch (error) {
            console.error('Request Error:', error);
            if (error.response) {
                const errorMessage = error.response.data?.error ||
                    error.response.data?.message ||
                    `Server error: ${error.response.status}`;
                toastify(errorMessage, 'error');
            } else if (error.request) {
                toastify('Network error: Unable to reach server', 'error');
            } else {
                toastify('Error saving bank details', 'error');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="tab-pane fade show active">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="mb-0">Payment Configuration</h4>
                <div className="d-flex align-items-center gap-3">
                    {/* Tab-level completion badge */}
                    {isCompleted && (
                        <span className="badge bg-success">
                            <i className="bi bi-check-circle me-1"></i>
                            Completed
                        </span>
                    )}

                    {/* Section completion indicator */}
                    {!isCompleted && (
                        <div className="text-muted small">
                            {getTabCompletionPercentage()}% complete
                        </div>
                    )}
                </div>
            </div>

            {isCompleted && !existingData && (
                <div className="alert alert-info mb-3">
                    <i className="bi bi-info-circle me-2"></i>
                    Payment configuration has already been added. You can update them if needed from the settings menu.
                </div>
            )}

            {/* Section Selection Tabs */}
            <div className="card mb-4">
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-6">
                            <div
                                className={`section-tab text-center p-3 border rounded cursor-pointer ${activeSection === 'bank' ? 'bg-primary text-white' : 'bg-light'
                                    }`}
                                onClick={() => setActiveSection('bank')}
                                style={{ cursor: 'pointer' }}
                            >
                                <i className="bi bi-bank me-2"></i>
                                Bank Account Details
                                {sectionCompleted.bank && (
                                    <span className="badge bg-success ms-2">
                                        <i className="bi bi-check"></i>
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div
                                className={`section-tab text-center p-3 border rounded cursor-pointer ${activeSection === 'payment' ? 'bg-primary text-white' : 'bg-light'
                                    }`}
                                onClick={() => setActiveSection('payment')}
                                style={{ cursor: 'pointer' }}
                            >
                                <i className="bi bi-credit-card me-2"></i>
                                Mpesa Payment Details
                                {sectionCompleted.payment && (
                                    <span className="badge bg-success ms-2">
                                        <i className="bi bi-check"></i>
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bank Details Section */}
            {activeSection === 'bank' && (
                <div className="card">
                    <div className="card-header">
                        <h5 className="mb-0">Bank Account Details</h5>
                    </div>
                    <div className="card-body">
                        <form onSubmit={handleBankDetailsSubmit}>
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label htmlFor="accountName" className="form-label">Account Holder Name *</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="accountName"
                                        value={bankDetails.accountName}
                                        onChange={(e) => setBankDetails({ ...bankDetails, accountName: e.target.value })}
                                        placeholder="Enter account holder name"
                                        required
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label htmlFor="accountNumber" className="form-label">Account Number *</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="accountNumber"
                                        value={bankDetails.accountNumber}
                                        onChange={(e) => setBankDetails({ ...bankDetails, accountNumber: e.target.value })}
                                        placeholder="1234567890"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label htmlFor="bankName" className="form-label">Bank Name *</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="bankName"
                                        value={bankDetails.bankName}
                                        onChange={(e) => setBankDetails({ ...bankDetails, bankName: e.target.value })}
                                        placeholder="e.g., Equity Bank"
                                        required
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label htmlFor="branchName" className="form-label">Branch Name *</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="branchName"
                                        value={bankDetails.branchName}
                                        onChange={(e) => setBankDetails({ ...bankDetails, branchName: e.target.value })}
                                        placeholder="e.g., Westlands Branch"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label htmlFor="branchCode" className="form-label">Branch Code</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="branchCode"
                                        value={bankDetails.branchCode}
                                        onChange={(e) => setBankDetails({ ...bankDetails, branchCode: e.target.value })}
                                        placeholder="Branch code"
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label htmlFor="bankCode" className="form-label">Bank Code</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="bankCode"
                                        value={bankDetails.bankCode}
                                        onChange={(e) => setBankDetails({ ...bankDetails, bankCode: e.target.value })}
                                        placeholder="Bank code"
                                    />
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label htmlFor="swiftCode" className="form-label">Swift Code</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="swiftCode"
                                        value={bankDetails.swiftCode}
                                        onChange={(e) => setBankDetails({ ...bankDetails, swiftCode: e.target.value })}
                                        placeholder="Swift code"
                                    />
                                </div>
                            </div>

                            <div className="form-check mb-3">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={bankDetails.isDefault}
                                    onChange={(e) => setBankDetails({ ...bankDetails, isDefault: e.target.checked })}
                                    id="defaultBankAccount"
                                />
                                <label className="form-check-label" htmlFor="defaultBankAccount">
                                    Set as Default Bank Account
                                </label>
                            </div>

                            <div className="text-end">
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={loading || !validateBankDetails()}
                                >
                                    {loading ? 'Saving...' : 'Save Bank Details'}
                                </button>
                            </div>

                            <small className="text-muted">* Required fields</small>
                        </form>

                        {sectionCompleted.bank && (
                            <div className="alert alert-success mt-3">
                                <i className="bi bi-check-circle-fill me-2"></i>
                                Bank details have been saved successfully!
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Payment Details Section */}
            {activeSection === 'payment' && (
                <div className="card">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">
                            Payment Gateway Details
                            {existingData?.paymentDetails && (
                                <span className="badge bg-info ms-2">Existing Details</span>
                            )}
                        </h5>
                        <button
                            className="btn btn-secondary"
                            onClick={fetchDefaultPaymentDetails}
                            disabled={isLoadingDefaults}
                        >
                            {isLoadingDefaults ? (
                                <span>
                                    <i className="ti ti-loader animate-spin me-2"></i>
                                    Loading...
                                </span>
                            ) : (
                                <span>
                                    <i className="ti ti-download me-2"></i>
                                    Use Default Settings
                                </span>
                            )}
                        </button>
                    </div>
                    <div className="card-body">
                        {apiError && (
                            <div className="alert alert-danger">
                                <i className="bi bi-exclamation-triangle me-2"></i>
                                {apiError}
                            </div>
                        )}

                        {existingData?.paymentDetails && (
                            <div className="alert alert-info mb-3">
                                <i className="bi bi-info-circle me-2"></i>
                                Existing payment details are pre-filled below. You can update them if needed.
                            </div>
                        )}

                        <div className="form-group mb-3">
                            <label className="form-label">
                                Short Code
                                <span className="text-danger">*</span>
                            </label>
                            <input
                                className="form-control"
                                type="text"
                                placeholder="Enter Short Code"
                                value={paymentDetails.shortCode}
                                onChange={(e) => setPaymentDetails({ ...paymentDetails, shortCode: e.target.value })}
                            />
                        </div>

                        <div className="form-group mb-3">
                            <label className="form-label">
                                Passkey
                                <span className="text-danger">*</span>
                            </label>
                            <input
                                className="form-control"
                                type="password"
                                placeholder="Enter Passkey"
                                value={paymentDetails.passkey}
                                onChange={(e) => setPaymentDetails({ ...paymentDetails, passkey: e.target.value })}
                            />
                        </div>

                        <div className="form-group mb-3">
                            <label className="form-label">
                                Authorization Key
                                <span className="text-danger">*</span>
                            </label>
                            <input
                                className="form-control"
                                type="password"
                                placeholder="Enter Authorization Key"
                                value={paymentDetails.authorizationKey}
                                onChange={(e) => setPaymentDetails({ ...paymentDetails, authorizationKey: e.target.value })}
                            />
                        </div>

                        <div className="form-group mb-3">
                            <label className="form-label">
                                Module
                                <span className="text-danger">*</span>
                            </label>
                            <select
                                className="form-control"
                                value={paymentDetails.module}
                                onChange={(e) => setPaymentDetails({ ...paymentDetails, module: e.target.value })}
                            >
                                <option value="">-- Select --</option>
                                <option value="All">All</option>
                                <option value="Water">Water</option>
                                <option value="Electricity">Electricity</option>
                                <option value="Levy">Levy</option>
                                <option value="Lease">Lease</option>
                            </select>
                        </div>

                        <div className="text-end">
                            <button
                                className="btn btn-light me-2"
                                onClick={handleCancel}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={handleMpesaDetailsSubmit}
                                disabled={isSubmitting || !validatePaymentDetails()}
                            >
                                {isSubmitting ? (
                                    <>
                                        <i className="ti ti-loader animate-spin me-2"></i>
                                        Saving...
                                    </>
                                ) : (
                                    existingData?.paymentDetails ? "Update Payment Details" : "Save Payment Details"
                                )}
                            </button>
                        </div>

                        {sectionCompleted.payment && (
                            <div className="alert alert-success mt-3">
                                <i className="bi bi-check-circle-fill me-2"></i>
                                Payment details have been saved successfully!
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default BankDetailsTab;